import { useCallback, useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import {
  getCallouts as getCalloutsQuery,
  getWBRCallouts,
} from "../graphql/queries";
import {
  addCallout as addCalloutMutation,
  updateCallout as updateCalloutMutation,
  deleteCallout as deleteCalloutMutation,
} from "../graphql/mutations";
import type {
  Callout,
  AddCalloutInput,
  UpdateCalloutInput,
  DeleteCalloutInput,
} from "../models/API";
import { useFeatureFlags } from "./useFeatureFlags";

const client = generateClient();

/**
 * Parse a `_callouts.html` body into placeholder `Callout` objects.
 *
 * Mirrors the backend `parse_callouts_html` (Python) used by the backfill DAG
 * and the deep-dive handler. The frontend version is read-only — it produces
 * `Callout`-shaped objects with synthetic `id` values (negative integers, kept
 * stable per session) and empty `cell_refs` since the legacy HTML does not
 * preserve cell-ref annotations.
 *
 * Used only as a fallback when `getCallouts` returns empty AND
 * `UNIFIED_CALLOUT_FALLBACK=true` (Req 12.3).
 */
function parseLegacyCalloutHtml(
  html: string,
  context: { teamId: number; week: string; reportName: string }
): Callout[] {
  const out: Callout[] = [];
  if (!html) return out;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const titles = doc.querySelectorAll(".page-section-title");
  const nowIso = new Date(0).toISOString(); // fallback timestamp — no real `created_at` is available
  let synthId = -1;

  titles.forEach((titleEl) => {
    const titleText = titleEl.textContent?.trim() ?? "";
    const m = titleText.match(/Page\s+(\d+)/i);
    const pageNum = m ? parseInt(m[1], 10) : 0;
    if (pageNum === 0) return;

    // Walk siblings until next page-section-title, collecting .callout-card elements.
    let sibling = titleEl.nextElementSibling;
    while (sibling && !sibling.classList.contains("page-section-title")) {
      // The `no-callout-card` variant means "AI did not flag this page" — skip it
      // because it is not a real callout (Req 12.3 + design Decision #9).
      if (
        sibling.classList.contains("callout-card") &&
        !sibling.classList.contains("no-callout-card")
      ) {
        const titleNode = sibling.querySelector(".headline");
        const cardTitle = titleNode?.textContent?.trim() ?? "";
        if (cardTitle) {
          const statusBadge = sibling.querySelector(".status-badge");
          const statusText = (
            statusBadge?.textContent?.trim() ?? "watch"
          ).toLowerCase();
          const status = statusText.includes("positive") ? "positive" : "watch";

          const trendBadge = sibling.querySelector(".trend-badge");
          const trend = trendBadge?.textContent?.trim() || null;

          const whyBody = sibling.querySelector(".why-body-text");
          const whyText = whyBody?.textContent?.trim() || null;

          out.push({
            __typename: "Callout",
            id: synthId,
            team_id: context.teamId,
            week: context.week,
            report_name: context.reportName,
            page_num: pageNum,
            source: "ai",
            title: cardTitle,
            status,
            trend,
            why_text: whyText,
            impact: null,
            card_number: out.filter((c) => c.page_num === pageNum).length + 1,
            cell_refs: [],
            creator_id: -1,
            creator_name: "VESL_WBR_Callout_Generation",
            updated_by: null,
            updated_by_name: null,
            created_at: nowIso,
            updated_at: nowIso,
            ai_generation_batch_id: null,
          });
          synthId -= 1;
        }
      }
      sibling = sibling.nextElementSibling;
    }
  });

  return out;
}

interface UseUnifiedCalloutsOptions {
  teamId: number;
  week: string;
  reportName?: string;
  /**
   * When defined, the hook will automatically `load()` on mount and whenever
   * any of the keys change. Defaults to `true`.
   */
  autoLoad?: boolean;
}

interface UseUnifiedCalloutsResult {
  callouts: Callout[];
  loading: boolean;
  error: string | null;
  /** True when `callouts` came from the legacy `_callouts.html` parse path. */
  readonlyFallback: boolean;
  load: () => Promise<void>;
  addCallout: (input: AddCalloutInput) => Promise<Callout | null>;
  updateCallout: (input: UpdateCalloutInput) => Promise<Callout | null>;
  deleteCallout: (input: DeleteCalloutInput) => Promise<boolean>;
}

/**
 * Hook for the unified WBR callout CRUD surface.
 *
 * - Reads via `getCallouts` GraphQL query.
 * - When the query returns empty AND the `UNIFIED_CALLOUT_FALLBACK` feature
 *   flag is `true`, falls back to parsing the legacy `_callouts.html` file
 *   surfaced by `getWBRCallouts`. Fallback callouts are read-only.
 * - Mutations use optimistic updates with rollback on failure.
 *
 * See spec tasks 11.2 / 11.3 / Req 6.1, 7.1, 12.1-12.5.
 */
export function useUnifiedCallouts(
  options: UseUnifiedCalloutsOptions
): UseUnifiedCalloutsResult {
  const { teamId, week, reportName, autoLoad = true } = options;
  const { flags } = useFeatureFlags();

  const [callouts, setCallouts] = useState<Callout[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [readonlyFallback, setReadonlyFallback] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!teamId || !week) return;
    setLoading(true);
    setError(null);
    try {
      const result = await client.graphql({
        query: getCalloutsQuery,
        variables: {
          input: {
            team_id: teamId,
            week,
            report_name: reportName ?? null,
          },
        },
      });
      const rows: Callout[] = (result as any).data?.getCallouts ?? [];
      if (rows.length > 0) {
        setCallouts(rows);
        setReadonlyFallback(false);
        return;
      }

      // Empty result — try fallback if the flag allows it AND we have a specific
      // report_name (the legacy HTML lives per-report).
      if (flags.UNIFIED_CALLOUT_FALLBACK && reportName) {
        try {
          const wbrResult = await client.graphql({
            query: getWBRCallouts,
            variables: {
              input: { team_id: teamId, week, report_name: reportName },
            },
          });
          const data: any = (wbrResult as any).data?.getWBRCallouts;
          if (data?.success && data.reports) {
            const report = data.reports.find(
              (r: any) => r.filename === reportName
            );
            const calloutHtmlUrl = report?.callout_html_url ?? null;
            if (calloutHtmlUrl) {
              const html = await fetch(calloutHtmlUrl).then((r) => r.text());
              const parsed = parseLegacyCalloutHtml(html, {
                teamId,
                week,
                reportName,
              });
              setCallouts(parsed);
              setReadonlyFallback(true);
              return;
            }
          }
        } catch (fbErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "[useUnifiedCallouts] fallback HTML parse failed",
            fbErr
          );
        }
      }

      setCallouts([]);
      setReadonlyFallback(false);
    } catch (err: any) {
      const msg =
        err?.errors?.[0]?.message ?? err?.message ?? "Failed to load callouts";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [teamId, week, reportName, flags.UNIFIED_CALLOUT_FALLBACK]);

  useEffect(() => {
    if (autoLoad) {
      void load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, teamId, week, reportName]);

  const addCallout = useCallback(
    async (input: AddCalloutInput): Promise<Callout | null> => {
      try {
        const result = await client.graphql({
          query: addCalloutMutation,
          variables: { input },
        });
        const row: Callout | null = (result as any).data?.addCallout ?? null;
        if (row) {
          setCallouts((prev) => [...prev, row]);
        }
        return row;
      } catch (err: any) {
        const msg =
          err?.errors?.[0]?.message ?? err?.message ?? "Failed to add callout";
        setError(msg);
        return null;
      }
    },
    []
  );

  const updateCallout = useCallback(
    async (input: UpdateCalloutInput): Promise<Callout | null> => {
      // Optimistic update with rollback.
      const prev = callouts;
      setCallouts((cs) =>
        cs.map((c) =>
          c.id === input.id
            ? {
                ...c,
                title: input.title ?? c.title,
                status: input.status ?? c.status,
                trend: input.trend ?? c.trend,
                why_text: input.why_text ?? c.why_text,
                cell_refs: input.cell_refs
                  ? input.cell_refs.map((r) => ({
                      __typename: "CellReference" as const,
                      ...r,
                    }))
                  : c.cell_refs,
                updated_by: input.updated_by,
                updated_at: new Date().toISOString(),
              }
            : c
        )
      );
      try {
        const result = await client.graphql({
          query: updateCalloutMutation,
          variables: { input },
        });
        const row: Callout | null = (result as any).data?.updateCallout ?? null;
        if (row) {
          // Replace optimistic entry with server-confirmed one.
          setCallouts((cs) => cs.map((c) => (c.id === row.id ? row : c)));
        }
        return row;
      } catch (err: any) {
        setCallouts(prev);
        const msg =
          err?.errors?.[0]?.message ??
          err?.message ??
          "Failed to update callout";
        setError(msg);
        return null;
      }
    },
    [callouts]
  );

  const deleteCallout = useCallback(
    async (input: DeleteCalloutInput): Promise<boolean> => {
      // Optimistic remove with rollback.
      const prev = callouts;
      setCallouts((cs) => cs.filter((c) => c.id !== input.id));
      try {
        await client.graphql({
          query: deleteCalloutMutation,
          variables: { input },
        });
        return true;
      } catch (err: any) {
        setCallouts(prev);
        const msg =
          err?.errors?.[0]?.message ??
          err?.message ??
          "Failed to delete callout";
        setError(msg);
        return false;
      }
    },
    [callouts]
  );

  return {
    callouts,
    loading,
    error,
    readonlyFallback,
    load,
    addCallout,
    updateCallout,
    deleteCallout,
  };
}
